import React from 'react';
import { clear, steps } from '@components/auth/registers/registerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/root-redux/store';

type MODAL_VIEWS =
  | 'REGISTER'
  | 'LOGIN_VIEW'
  | 'FORGOT_VIEW'
  | 'RESET_PASSWORD_VIEW'
  | 'OTP_LOGIN'
  | 'ADD_OR_UPDATE_ADDRESS'
  | 'ADD_OR_UPDATE_CHECKOUT_CONTACT'
  | 'ADD_OR_UPDATE_PROFILE_CONTACT'
  | 'DELETE_ADDRESS'
  | 'PRODUCT_DETAILS'
  | 'SHOP_INFO'
  | 'RATES_SHOP'
  | 'ZOOM_IMAGE_POPUP'
  | 'CONFIRM_CANCEL_CLAIMS_POPUP'
  | 'SEARCH_POPUP'
  | 'SERVICE_DETAIL_POPUP'
  | 'SHOP_NOTE_POPUP'
  | 'ORDER_PRODUCT_DELETED_POPUP'
  | 'DELETE_SHOP_ORDER_POPUP'
  | 'UPDATE_ICO_POPUP'
  | 'POLICY_POPUP'
  | 'CREATE_CLAIM_POPUP';

interface State {
  view?: MODAL_VIEWS;
  data?: any;
  isOpen: boolean;
}
type Action =
  | { type: 'open'; view?: MODAL_VIEWS; payload?: any }
  | { type: 'close' };

const initialState: State = {
  view: undefined,
  isOpen: false,
  data: null,
};

function modalReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        view: action.view,
        data: action.payload,
        isOpen: true,
      };
    case 'close':
      return {
        ...state,
        view: undefined,
        data: null,
        isOpen: false,
      };
    default:
      throw new Error('Unknown Modal Action!');
  }
}

const ModalStateContext = React.createContext<State>(initialState);
ModalStateContext.displayName = 'ModalStateContext';
const ModalActionContext = React.createContext<
  React.Dispatch<Action> | undefined
>(undefined);
ModalActionContext.displayName = 'ModalActionContext';

export const ModalProvider: React.FC = ({ children }) => {
  const [state, dispatch] = React.useReducer(modalReducer, initialState);
  return (
    <ModalStateContext.Provider value={state}>
      <ModalActionContext.Provider value={dispatch}>
        {children}
      </ModalActionContext.Provider>
    </ModalStateContext.Provider>
  );
};

export function useModalState() {
  const context = React.useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error(`useModalState must be used within a ModalProvider`);
  }
  return context;
}

export function useModalAction() {
  const dispatch = React.useContext(ModalActionContext);
  const registerState = useSelector((state: RootState) => state.register);
  const reduxDispatch = useDispatch();

  if (dispatch === undefined) {
    throw new Error(`useModalAction must be used within a ModalProvider`);
  }
  return {
    openModal(view?: MODAL_VIEWS, payload?: unknown) {
      dispatch({ type: 'open', view, payload });
    },
    closeModal() {
      if(registerState.currentStep == steps.slice(-1).pop()) reduxDispatch(clear());
      dispatch({ type: 'close' });
    },
  };
}
